import React, { FC } from 'react';
import { LuList } from 'react-icons/lu';
import FieldError from '@/components/elements/FieldError';
import AppButton from '@/components/atoms/AppButton';
import SelectableChip from '@/components/elements/SelectableChip';
import { Text, Caption } from '@/components';
import type { SelectableChipTheme } from '@/components';
import { PROVIDER_CONFIG_PROPS } from '@/constant/provider/provider';
import { tid } from '@/utils/testId';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MultiSelectOption {
  value: string;
  label: string;
}

export type MultiSelectTheme = SelectableChipTheme;

export interface MultiSelectProps {
  /** Selectable options to render as chips */
  options: MultiSelectOption[];
  /** Currently selected values */
  selectedValues: string[];
  /** Fires with the full new selection array on every change */
  onChange: (values: string[]) => void;
  /** "multi" allows multiple selections (default); "single" enforces one at a time */
  selectionMode?: 'multi' | 'single';
  /** Card heading — default: "Select Items" */
  title?: string;
  /** Subtext beneath the heading — default: "Select one or more" */
  subtitle?: string;
  /** Node rendered inside the coloured badge icon — default: list SVG */
  icon?: React.ReactNode;
  /** Colour theme for the card, chips and badges */
  theme?: MultiSelectTheme;
  /** Inline validation error message */
  error?: string;
  /** Show red asterisk next to title */
  required?: boolean;
  /** Extra Tailwind classes applied to the outer wrapper */
  className?: string;
  /** Prefix for data-testid on chips and action buttons */
  testIdPrefix?: string;
  /** Disables all interaction and dims the component */
  disabled?: boolean;
}

// ─── Theme tokens (card-level only — chip theming lives in SelectableChip) ────

const THEME: Record<
  MultiSelectTheme,
  {
    wrapper: string;
    divider: string;
    icon: string;
    badge: string;
  }
> = {
  blue: {
    wrapper:
      'from-blue-50 via-white to-indigo-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 border-blue-200 dark:border-blue-700',
    divider: 'border-blue-100 dark:border-blue-800',
    icon: 'from-blue-500 to-indigo-600',
    badge:
      'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700',
  },
  purple: {
    wrapper:
      'from-purple-50 via-white to-pink-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 border-purple-200 dark:border-purple-700',
    divider: 'border-purple-100 dark:border-purple-800',
    icon: 'from-purple-500 to-pink-600',
    badge:
      'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700',
  },
  green: {
    wrapper:
      'from-green-50 via-white to-emerald-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 border-green-200 dark:border-green-700',
    divider: 'border-green-100 dark:border-green-800',
    icon: 'from-green-500 to-teal-600',
    badge:
      'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700',
  },
  orange: {
    wrapper:
      'from-orange-50 via-white to-amber-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 border-orange-200 dark:border-orange-700',
    divider: 'border-orange-100 dark:border-orange-800',
    icon: 'from-orange-500 to-amber-600',
    badge:
      'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-700',
  },
  red: {
    wrapper:
      'from-red-50 via-white to-rose-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 border-red-200 dark:border-red-700',
    divider: 'border-red-100 dark:border-red-800',
    icon: 'from-red-500 to-rose-600',
    badge:
      'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700',
  },
  primary: {
    wrapper:
      'from-orange-50 via-white to-amber-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 border-primary/40 dark:border-primary/30',
    divider: 'border-primary/20 dark:border-primary/20',
    icon: 'from-primary to-secondary',
    badge: 'bg-primary/10 dark:bg-primary/20 text-primary border-primary/30',
  },
};

const DEFAULT_ICON = <LuList className="w-4 h-4 text-white" />;

// ─── MultiSelect Component (internal) ───────────────────────────────────────────────────────────────────────────────────────────────────────────────

const MultiSelect: FC<MultiSelectProps> = ({
  options = [],
  selectedValues = [],
  onChange,
  selectionMode = 'multi',
  title = 'Select Items',
  subtitle = 'Select one or more',
  icon = DEFAULT_ICON,
  theme = 'blue',
  error,
  required,
  className = '',
  testIdPrefix,
  disabled = false,
}) => {
  const t = THEME[theme];

  const toggle = (value: string) => {
    if (selectionMode === 'single') {
      onChange(selectedValues.includes(value) ? [] : [value]);
    } else if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  return (
    <div
      className={`mt-6 p-5 rounded-2xl border shadow-sm bg-gradient-to-br ${t.wrapper} ${className}`}
    >
      {/* Header */}
      <div
        className={`flex items-center gap-3 mb-4 pb-3 border-b ${t.divider}`}
      >
        <div
          className={`w-8 h-8 rounded-lg bg-gradient-to-br flex items-center justify-center shadow-md flex-shrink-0 ${t.icon}`}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <Text as="h4" size="xl" weight="bold" color="default" leading="tight">
            {title}
            {required && <span className="text-red-500 ml-0.5">*</span>}
          </Text>
          <Caption size="base" color="subtle" className="mt-0.5">
            {subtitle}
          </Caption>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {selectedValues.length > 0 && (
            <>
              <span
                className={`text-xs font-bold px-2 py-0.5 rounded-full border ${t.badge}`}
              >
                {selectedValues.length}/{options.length}
              </span>
              <AppButton
                type="button"
                variant="danger"
                size="xs"
                shape="pill"
                onClick={clearAll}
                disabled={disabled}
                data-testid={testIdPrefix ? `${testIdPrefix}-clear-all` : undefined}
              >
                Clear all
              </AppButton>
            </>
          )}
        </div>
      </div>

      {/* Chip Grid */}
      <div className={`relative flex flex-wrap gap-2${disabled ? ' opacity-50' : ''}`}>
        {disabled && <div className="absolute inset-0 z-10 cursor-not-allowed" />}
        {options.map((option) => (
          <SelectableChip
            key={option.value}
            label={option.label}
            selected={selectedValues.includes(option.value)}
            onClick={() => toggle(option.value)}
            theme={theme}
            selectionType="checkbox"
            testId={testIdPrefix ? `${testIdPrefix}-chip-${option.value}` : undefined}
          />
        ))}
      </div>

      {/* Validation Error */}
      <FieldError message={error} />
    </div>
  );
};

// ─── VDI Type Options ─────────────────────────────────────────────────────────

/** Built-in VDI type options used across provider config sections */
export const VDI_TYPE_OPTIONS: MultiSelectOption[] = [
  { value: 'mmsMarkets', label: 'mmsMarkets' },
  { value: 'mmsDevices', label: 'mmsDevices' },
  { value: 'mmsProducts', label: 'mmsProducts' },
  { value: 'mmsSales', label: 'mmsSales' },
];

// ─── VdiTypesMultiSelect Component ───────────────────────────────────────────

interface VdiTypesMultiSelectProps {
  selectedTypes: string[];
  onChange: (types: string[]) => void;
  theme?: MultiSelectTheme;
  error?: string;
  required?: boolean;
  testIdPrefix?: string;
  disabled?: boolean;
}

const VdiTypesMultiSelect: FC<VdiTypesMultiSelectProps> = ({
  selectedTypes,
  onChange,
  theme = 'blue',
  error,
  required,
  testIdPrefix,
  disabled = false,
}) => (
  <MultiSelect
    options={VDI_TYPE_OPTIONS}
    selectedValues={selectedTypes}
    onChange={onChange}
    title={PROVIDER_CONFIG_PROPS.VDI_TYPES_TITLE}
    subtitle={PROVIDER_CONFIG_PROPS.VDI_TYPES_SUBTITLE}
    theme={theme}
    error={error}
    required={required}
    testIdPrefix={testIdPrefix}
    disabled={disabled}
  />
);

export default VdiTypesMultiSelect;
